import { create } from 'zustand';
import { localClient, type LocalClient } from '@/data/local-client';
import type { AuthUser, LocalSession } from '@/data/local-client.types';

/**
 * @fileoverview Core Data Store (Zustand)
 * @description
 * React/Zustand port of the Angular `DataService`. Provides centralized access
 * to the local-storage data client and tracks authentication state. The public
 * surface (method names + state) is reproduced 1:1 so consumers keep working.
 *
 * @dependencies
 * - local-client (localStorage-backed)
 */

// Reference the single shared client (see src/data/local-client.ts). The
// imperative dataService uses the same instance, so there is one client/auth.

interface DataState {
  // Reactive state (was Angular signals)
  currentUser: AuthUser | null;
  currentSession: LocalSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  /** Get the local data client instance. */
  client: () => LocalClient;
  /** Get current user ID. */
  getCurrentUserId: () => string | null;
  /** Get current user email. */
  getCurrentUserEmail: () => string | null;
  /** Check if user is authenticated. */
  isUserAuthenticated: () => boolean;
  /** Refresh the current session. */
  refreshSession: () => Promise<{ session: LocalSession | null; error: any }>;
  /** Initialize auth listener + load initial session (was the Angular constructor). */
  initialize: () => void;
}

export const useDataStore = create<DataState>((set, get) => ({
  currentUser: null,
  currentSession: null,
  isAuthenticated: false,
  isLoading: true,

  client: () => localClient,

  getCurrentUserId: () => get().currentUser?.id ?? null,

  getCurrentUserEmail: () => get().currentUser?.email ?? null,

  isUserAuthenticated: () => get().isAuthenticated,

  refreshSession: async () => {
    const { data, error } = await localClient.auth.refreshSession();
    return { session: data?.session ?? null, error };
  },

  initialize: () => {
    // initializeAuthListener()
    localClient.auth.onAuthStateChange((_event, session) => {
      set({
        currentSession: session,
        currentUser: session?.user ?? null,
        isAuthenticated: !!session,
      });
    });

    // loadSession()
    void (async () => {
      try {
        set({ isLoading: true });
        const {
          data: { session },
        } = await localClient.auth.getSession();
        set({
          currentSession: session,
          currentUser: session?.user ?? null,
          isAuthenticated: !!session,
        });
      } catch (error: any) {
        console.error('Unexpected error loading session:', error);
        set({ currentSession: null, currentUser: null, isAuthenticated: false });
      } finally {
        set({ isLoading: false });
      }
    })();
  },
}));

// Run init once at module load (mirrors Angular's root-singleton constructor).
useDataStore.getState().initialize();
